import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { prettyLabel } from '../../lib/labels'
import { alphaToPngBase64, describeAlpha, strokesToAlpha } from '../../lib/segment'
import { visualCenter } from '../../lib/maskCenter'
import type { WorkspaceMask, WorkspacePhoto } from '../../lib/wstypes'

/**
 * The canvas: your room, with everything in it already found (D38), shown the way a person
 * would point at it (D39).
 *
 * The photo arrives pre-understood — detection has cut a mask for the vanity, the mirror, the
 * shower — and each one wears a small named chip. Tapping the chip, or the thing itself,
 * selects it and the rail fills with products.
 *
 * What selection LOOKS like changed on 2026-08-23. The first build painted the mask itself —
 * a translucent blue fill in the mask's exact ragged shape — and every honest imperfection of
 * a segmentation mask (the bite a towel takes out of a vanity, the hole a soap bottle
 * punches) rendered as a glitch. Now the mask is plumbing: it decides what a tap means and
 * tells the server what to change, and the person sees a **spotlight** — the rest of the
 * photo gently dims behind a feathered edge and the chosen thing stays bright. A feathered
 * spotlight is forgiving of every mask flaw a hard-edged fill advertises.
 *
 * Chips lay themselves out: anchored to the item's centre, then nudged apart until none of
 * them overlap — three chips stacked unreadably on a vanity was the day-one field report.
 */

export type CanvasMode = 'browse' | 'brush' | 'erase'

interface Props {
  photo: WorkspacePhoto
  masks: WorkspaceMask[]
  activeMaskId: string | null
  mode: CanvasMode
  /** A finished render shown over the photo, with the before/after divider. */
  overlayUrl?: string | null
  compare?: boolean
  /** Detection has been asked and has not answered yet — show the scan. */
  detecting?: boolean
  /** A preview is being made right now — shimmer over the photo. */
  rendering?: boolean
  disabled?: boolean
  onSelectMask: (maskId: string | null) => void
  onMaskDrawn: (payload: {
    png_base64: string
    bbox: [number, number, number, number]
    area: number
    method: 'brush'
  }) => void
}

function boxOf(mask: WorkspaceMask): [number, number, number, number] {
  const [x = 0, y = 0, w = 0, h = 0] = mask.bbox
  return [x, y, w, h]
}

interface Stroke {
  points: { x: number; y: number }[]
  radius: number
  erase: boolean
}

/** Brush radius as a fraction of the photo's short edge, so it feels the same on any photo. */
const BRUSH_FRACTION = 0.035
/** Hit-maps are sampled at most this wide — finger-precision, at 1/25th the memory. */
const HIT_EDGE = 400
/** The spotlight's soft edge: the mask at 1/this scale, upsampled with smoothing. The
 *  browser's bilinear upsample IS the feather — no ctx.filter, which iOS<18 lacks. */
const FEATHER_SCALE = 14

/** What one mask needs at draw and tap time, built once from its PNG. */
interface MaskAsset {
  //: The label anchor, in photo pixels: the mask's visual centre (see lib/maskCenter), which
  //: for a floor is actual floor — the bbox centre was the toilet (field report 2026-08-24).
  center: { x: number; y: number } | null
  /** Downscaled hit-map: 1 where the mask is, row-major. */
  hit: Uint8Array
  hitW: number
  hitH: number
  /** The mask as opaque-in-shape alpha, full resolution — the spotlight's crisp core. */
  maskCanvas: HTMLCanvasElement
  /** Tiny copy whose smoothed upscale is the feathered edge. */
  soft: HTMLCanvasElement
  /** The tiny copy tinted white, for the glow just outside the edge. */
  softGlow: HTMLCanvasElement
  width: number
  height: number
}

function buildAsset(img: HTMLImageElement): MaskAsset {
  const { naturalWidth: w, naturalHeight: h } = img

  // The PNG is opaque grayscale (white object on black), so alpha must come from luminance.
  const source = document.createElement('canvas')
  source.width = w
  source.height = h
  const sctx = source.getContext('2d', { willReadFrequently: true })!
  sctx.drawImage(img, 0, 0)
  const data = sctx.getImageData(0, 0, w, h)
  const solid = sctx.createImageData(w, h)
  for (let i = 0; i < w * h; i += 1) {
    if ((data.data[i * 4] ?? 0) > 127) solid.data[i * 4 + 3] = 255
  }
  const maskCanvas = document.createElement('canvas')
  maskCanvas.width = w
  maskCanvas.height = h
  maskCanvas.getContext('2d')!.putImageData(solid, 0, 0)

  const sw = Math.max(2, Math.round(w / FEATHER_SCALE))
  const sh = Math.max(2, Math.round(h / FEATHER_SCALE))
  const soft = document.createElement('canvas')
  soft.width = sw
  soft.height = sh
  const softCtx = soft.getContext('2d')!
  softCtx.imageSmoothingEnabled = true
  softCtx.drawImage(maskCanvas, 0, 0, sw, sh)

  const softGlow = document.createElement('canvas')
  softGlow.width = sw
  softGlow.height = sh
  const glowCtx = softGlow.getContext('2d')!
  glowCtx.drawImage(soft, 0, 0)
  glowCtx.globalCompositeOperation = 'source-in'
  glowCtx.fillStyle = 'rgba(255,255,255,0.55)'
  glowCtx.fillRect(0, 0, sw, sh)

  const hitW = Math.max(1, Math.min(HIT_EDGE, w))
  const hitH = Math.max(1, Math.round((h / w) * hitW))
  const small = document.createElement('canvas')
  small.width = hitW
  small.height = hitH
  const hctx = small.getContext('2d', { willReadFrequently: true })!
  hctx.drawImage(maskCanvas, 0, 0, hitW, hitH)
  const hitData = hctx.getImageData(0, 0, hitW, hitH)
  const hit = new Uint8Array(hitW * hitH)
  for (let i = 0; i < hit.length; i += 1) {
    hit[i] = (hitData.data[i * 4 + 3] ?? 0) > 127 ? 1 : 0
  }

  const gridCenter = visualCenter(hit, hitW, hitH)
  const center = gridCenter
    ? { x: (gridCenter.x / hitW) * w, y: (gridCenter.y / hitH) * h }
    : null

  return { hit, hitW, hitH, center, maskCanvas, soft, softGlow, width: w, height: h }
}

/** Chip geometry, all in canvas CSS pixels. Height matches the button's min-h-11. */
const CHIP_H = 44
const CHIP_GAP = 6

interface ChipSpot {
  x: number
  y: number
  w: number
}

/**
 * Place one chip per mask so that none of them overlap.
 *
 * Anchored at the item's centre, then nudged up or down in whole chip-heights until the spot
 * is free. Small items go first — a faucet's chip belongs on the faucet; the floor's chip can
 * live anywhere on the floor.
 */
function layoutChips(
  entries: {
    id: string
    label: string
    bbox: [number, number, number, number]
    area: number
    center?: { x: number; y: number } | null
    onMask?: (photoX: number, photoY: number) => boolean
  }[],
  box: { width: number; height: number; offsetX: number; offsetY: number; scale: number },
): Map<string, ChipSpot> {
  const placed: { x: number; y: number; w: number }[] = []
  const out = new Map<string, ChipSpot>()
  const photoTop = box.offsetY + 4
  const photoBottom = box.offsetY + (box.height - 2 * box.offsetY) - 4

  const collides = (x: number, y: number, w: number) =>
    placed.some(
      (other) =>
        Math.abs(x - other.x) < (w + other.w) / 2 + CHIP_GAP &&
        Math.abs(y - other.y) < CHIP_H + CHIP_GAP,
    )

  for (const entry of [...entries].sort((a, b) => a.area - b.area)) {
    const [bx, by, bw, bh] = entry.bbox
    const w = Math.max(60, Math.min(entry.label.length * 7.6 + 34, box.width - 16))
    // Anchor at the mask's visual centre when it is known: the bbox centre of an irregular
    // mask is regularly on some OTHER item (a floor's box centre is the toilet standing on
    // it — field report 2026-08-24). The bbox stays as the fallback for unreadable masks.
    const anchorX = entry.center ? entry.center.x : bx + bw / 2
    const anchorY = entry.center ? entry.center.y : by + bh / 2
    const idealX = box.offsetX + anchorX * box.scale
    const x = Math.min(Math.max(idealX, w / 2 + 4), Math.max(box.width - w / 2 - 4, w / 2 + 4))
    const idealY = box.offsetY + anchorY * box.scale

    // Collision nudging searches in two dimensions — the 2026-08-24 field capture proved a
    // vertical-only walk cannot seat eight chips whose anchors share the photo's bottom
    // third: it both overlapped ("Sink" on "Vanity top") and flung chips onto other items.
    // Candidates are ordered nearest-first; among collision-free spots the first that still
    // sits over the entry's own mask wins, since a chip pushed onto a neighbouring item reads
    // as a label for that item, whatever it says.
    let spot: { x: number; y: number } | null = null
    let offMask: { x: number; y: number } | null = null
    const clampX = (value: number) =>
      Math.min(Math.max(value, w / 2 + 4), Math.max(box.width - w / 2 - 4, w / 2 + 4))
    outer: for (const stepY of [0, -1, 1, -2, 2, -3, 3, -4, 4]) {
      for (const stepX of [0, -1, 1, -2, 2]) {
        const cx = clampX(x + stepX * (w * 0.55 + CHIP_GAP))
        const cy = Math.min(
          Math.max(idealY + stepY * (CHIP_H + CHIP_GAP), photoTop + CHIP_H / 2),
          Math.max(photoBottom - CHIP_H / 2, photoTop + CHIP_H / 2),
        )
        if (collides(cx, cy, w)) continue
        const photoX = (cx - box.offsetX) / box.scale
        const photoY = (cy - box.offsetY) / box.scale
        if (!entry.onMask || entry.onMask(photoX, photoY)) {
          spot = { x: cx, y: cy }
          break outer
        }
        offMask = offMask ?? { x: cx, y: cy }
      }
    }
    spot = spot ?? offMask
    // Forty-five tries not finding air means the photo is wallpapered in chips; take the
    // ideal spot rather than dropping the item — an overlapped chip still works, a missing
    // one does not.
    const fx = spot?.x ?? x
    const y = spot?.y ?? idealY
    placed.push({ x: fx, y, w })
    out.set(entry.id, { x: fx, y, w })
  }
  return out
}

const reducedMotion = () =>
  typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches

export function Canvas({
  photo,
  masks,
  activeMaskId,
  mode,
  overlayUrl,
  compare = false,
  detecting = false,
  rendering = false,
  disabled,
  onSelectMask,
  onMaskDrawn,
}: Props) {
  const wrapRef = useRef<HTMLDivElement>(null)
  const spotRef = useRef<HTMLCanvasElement>(null)
  const overlayRef = useRef<HTMLCanvasElement>(null)
  const [box, setBox] = useState({ width: 0, height: 0, offsetX: 0, offsetY: 0, scale: 1 })
  const [strokes, setStrokes] = useState<Stroke[]>([])
  const [sliderX, setSliderX] = useState(0.5)
  const [assets, setAssets] = useState<Map<string, MaskAsset>>(new Map())
  const alphaRef = useRef<Uint8Array | null>(null)
  const drawingRef = useRef(false)
  const scrubbingRef = useRef(false)
  const revealRef = useRef(0)

  // A fresh preview announces itself: the divider sweeps in from the right edge to the
  // midline, so the change literally wipes across the room. (Reduced motion: it just appears.)
  useEffect(() => {
    if (!overlayUrl) return
    cancelAnimationFrame(revealRef.current)
    if (reducedMotion()) {
      setSliderX(0.5)
      return
    }
    const started = performance.now()
    const duration = 700
    const tick = (now: number) => {
      const t = Math.min((now - started) / duration, 1)
      const eased = 1 - Math.pow(1 - t, 3)
      setSliderX(1 - 0.5 * eased)
      if (t < 1) revealRef.current = requestAnimationFrame(tick)
    }
    revealRef.current = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(revealRef.current)
  }, [overlayUrl])

  // --------------------------------------------------------------- layout & transform
  useEffect(() => {
    const element = wrapRef.current
    if (!element) return
    const measure = () => {
      const rect = element.getBoundingClientRect()
      const scale = Math.min(rect.width / photo.width, rect.height / photo.height)
      setBox({
        width: rect.width,
        height: rect.height,
        offsetX: (rect.width - photo.width * scale) / 2,
        offsetY: (rect.height - photo.height * scale) / 2,
        scale,
      })
    }
    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(element)
    return () => observer.disconnect()
  }, [photo.width, photo.height])

  const toPhoto = useCallback(
    (clientX: number, clientY: number) => {
      const rect = wrapRef.current?.getBoundingClientRect()
      if (!rect || box.scale === 0) return null
      const x = (clientX - rect.left - box.offsetX) / box.scale
      const y = (clientY - rect.top - box.offsetY) / box.scale
      if (x < 0 || y < 0 || x > photo.width || y > photo.height) return null
      return { x, y }
    },
    [box, photo.width, photo.height],
  )

  // ------------------------------------------------------------------ mask assets
  const photoMasks = useMemo(
    () => masks.filter((mask) => mask.photo_id === photo.id),
    [masks, photo.id],
  )

  useEffect(() => {
    let cancelled = false
    for (const mask of photoMasks) {
      if (assets.has(mask.id)) continue
      const img = new Image()
      // The mask host answers CORS for this origin; without the attribute the canvas taints
      // on first drawImage and every later readback throws.
      img.crossOrigin = 'anonymous'
      img.onload = () => {
        if (cancelled) return
        try {
          const asset = buildAsset(img)
          setAssets((current) => new Map(current).set(mask.id, asset))
        } catch {
          // A mask that cannot be read still has its chip (positioned by bbox); taps on the
          // chip work, region taps fall through to the bbox test.
        }
      }
      img.src = mask.url
    }
    return () => {
      cancelled = true
    }
  }, [photoMasks, assets])

  // ------------------------------------------------------------------ spotlight
  /**
   * The selection, as light: the photo dims everywhere but the chosen item, behind a wide
   * feathered edge, with a whisper of white glow just past the item's outline. Drawn once per
   * selection; the fade in and out is CSS opacity on the canvas element itself, which the
   * compositor animates without repainting.
   */
  const drawSpotlight = useCallback(() => {
    const canvas = spotRef.current
    if (!canvas) return
    const dpr = Math.min(window.devicePixelRatio || 1, 2)
    canvas.width = Math.round(box.width * dpr)
    canvas.height = Math.round(box.height * dpr)
    const ctx = canvas.getContext('2d')
    if (!ctx || box.width === 0) return
    ctx.setTransform(canvas.width / box.width, 0, 0, canvas.height / box.height, 0, 0)
    ctx.clearRect(0, 0, box.width, box.height)

    const asset = activeMaskId ? assets.get(activeMaskId) : null
    if (!asset) return
    const w = photo.width * box.scale
    const h = photo.height * box.scale
    ctx.imageSmoothingEnabled = true
    ctx.imageSmoothingQuality = 'high'

    // Dim the room…
    ctx.fillStyle = 'rgba(10, 21, 37, 0.46)'
    ctx.fillRect(box.offsetX, box.offsetY, w, h)
    // …open a feathered window where the item is…
    ctx.globalCompositeOperation = 'destination-out'
    ctx.drawImage(asset.soft, box.offsetX, box.offsetY, w, h)
    ctx.drawImage(asset.maskCanvas, box.offsetX, box.offsetY, w, h)
    // …and breathe a little light just outside its edge.
    ctx.globalCompositeOperation = 'source-over'
    ctx.drawImage(asset.softGlow, box.offsetX, box.offsetY, w, h)
    ctx.globalCompositeOperation = 'destination-out'
    ctx.drawImage(asset.maskCanvas, box.offsetX, box.offsetY, w, h)
    ctx.globalCompositeOperation = 'source-over'
  }, [box, assets, activeMaskId, photo.width, photo.height])

  useEffect(() => {
    drawSpotlight()
  }, [drawSpotlight])

  // ------------------------------------------------------------------------ drawing
  const redraw = useCallback(
    (alpha: Uint8Array | null) => {
      const canvas = overlayRef.current
      if (!canvas) return
      const dpr = Math.min(window.devicePixelRatio || 1, 2)
      canvas.width = Math.round(box.width * dpr)
      canvas.height = Math.round(box.height * dpr)
      const context = canvas.getContext('2d')
      if (!context) return
      // The exact backing/CSS ratio, not `dpr`: the backing store is rounded to whole pixels
      // and the element is not.
      context.setTransform(canvas.width / box.width, 0, 0, canvas.height / box.height, 0, 0)
      context.clearRect(0, 0, box.width, box.height)
      if (alpha) drawAlpha(context, alpha, photo.width, photo.height, box)
    },
    [box, photo.width, photo.height],
  )

  useEffect(() => {
    redraw(alphaRef.current)
  }, [redraw])

  // Leaving brush mode drops any half-drawn stroke.
  useEffect(() => {
    if (mode === 'browse') {
      setStrokes([])
      alphaRef.current = null
    }
  }, [mode])

  // -------------------------------------------------------------------- interactions
  const scrub = useCallback((event: React.PointerEvent) => {
    const rect = wrapRef.current?.getBoundingClientRect()
    if (!rect || rect.width === 0) return
    setSliderX(Math.min(1, Math.max(0, (event.clientX - rect.left) / rect.width)))
  }, [])

  const hitTest = useCallback(
    (point: { x: number; y: number }): WorkspaceMask | null => {
      // Smallest first, so a faucet inside a vanity's box is still tappable.
      const ordered = [...photoMasks].sort((a, b) => a.area_px - b.area_px)
      for (const mask of ordered) {
        const asset = assets.get(mask.id)
        if (asset) {
          const hx = Math.floor((point.x / photo.width) * asset.hitW)
          const hy = Math.floor((point.y / photo.height) * asset.hitH)
          if (asset.hit[hy * asset.hitW + hx]) return mask
          continue
        }
        const [mx, my, mw, mh] = boxOf(mask)
        if (point.x >= mx && point.x <= mx + mw && point.y >= my && point.y <= my + mh) {
          return mask
        }
      }
      return null
    },
    [photoMasks, assets, photo.width, photo.height],
  )

  const handlePointerDown = useCallback(
    (event: React.PointerEvent) => {
      // In compare mode the photo itself is the slider. Not gated on `disabled` — comparing
      // is reading, not editing, and must work in a sent session.
      if (compare) {
        if (overlayUrl) {
          cancelAnimationFrame(revealRef.current)
          scrubbingRef.current = true
          ;(event.target as HTMLElement).setPointerCapture?.(event.pointerId)
          scrub(event)
        }
        return
      }
      if (disabled) return
      const point = toPhoto(event.clientX, event.clientY)
      if (!point) return
      ;(event.target as HTMLElement).setPointerCapture?.(event.pointerId)

      if (mode === 'browse') {
        onSelectMask(hitTest(point)?.id ?? null)
        return
      }

      drawingRef.current = true
      const radius = (Math.min(photo.width, photo.height) * BRUSH_FRACTION) / 2
      setStrokes((current) => [...current, { points: [point], radius, erase: mode === 'erase' }])
    },
    [
      compare,
      overlayUrl,
      scrub,
      disabled,
      toPhoto,
      mode,
      onSelectMask,
      hitTest,
      photo.width,
      photo.height,
    ],
  )

  const handlePointerMove = useCallback(
    (event: React.PointerEvent) => {
      if (scrubbingRef.current) {
        scrub(event)
        return
      }
      if (!drawingRef.current || mode === 'browse') return
      const point = toPhoto(event.clientX, event.clientY)
      if (!point) return
      setStrokes((current) => {
        const last = current[current.length - 1]
        if (!last) return current
        const updated: Stroke = { ...last, points: [...last.points, point] }
        const next = [...current.slice(0, -1), updated]
        const alpha = strokesToAlpha(next, photo.width, photo.height)
        alphaRef.current = alpha
        redraw(alpha)
        return next
      })
    },
    [scrub, mode, toPhoto, photo.width, photo.height, redraw],
  )

  const handlePointerUp = useCallback(() => {
    drawingRef.current = false
    scrubbingRef.current = false
  }, [])

  const commit = useCallback(async () => {
    const alpha = alphaRef.current
    if (!alpha) return
    const { bbox, area } = describeAlpha(alpha, photo.width, photo.height)
    if (area < 400) return
    const png_base64 = await alphaToPngBase64(alpha, photo.width, photo.height)
    onMaskDrawn({ png_base64, bbox, area, method: 'brush' })
    setStrokes([])
    alphaRef.current = null
    redraw(null)
  }, [photo.width, photo.height, onMaskDrawn, redraw])

  const clear = useCallback(() => {
    setStrokes([])
    alphaRef.current = null
    redraw(null)
  }, [redraw])

  const hasStrokes = strokes.length > 0
  const showChips = mode === 'browse' && !compare
  const showSpotlight = Boolean(activeMaskId) && mode === 'browse' && !compare && !overlayUrl

  const chipSpots = useMemo(
    () =>
      layoutChips(
        photoMasks
          .filter((mask) => mask.label || mask.slot)
          .map((mask) => {
            const asset = assets.get(mask.id)
            return {
              id: mask.id,
              label: prettyLabel(mask.label ?? mask.slot ?? ''),
              bbox: boxOf(mask),
              area: mask.area_px,
              center: asset?.center ?? null,
              onMask: asset
                ? (photoX: number, photoY: number) => {
                    const hx = Math.floor((photoX / asset.width) * asset.hitW)
                    const hy = Math.floor((photoY / asset.height) * asset.hitH)
                    if (hx < 0 || hy < 0 || hx >= asset.hitW || hy >= asset.hitH) return false
                    return asset.hit[hy * asset.hitW + hx] === 1
                  }
                : undefined,
            }
          }),
        box,
      ),
    [photoMasks, box, assets],
  )

  const handleLeft = Math.min(Math.max(sliderX * box.width, 30), Math.max(box.width - 30, 30))

  return (
    <div className="relative flex h-full w-full flex-col">
      <div
        ref={wrapRef}
        style={{
          aspectRatio: `${photo.width} / ${photo.height}`,
          maxWidth: `calc(var(--canvas-max-h) * ${photo.width} / ${photo.height})`,
        }}
        className="relative mx-auto max-h-[var(--canvas-max-h)] w-full flex-1 touch-none overflow-hidden rounded-img bg-navy-2 [--canvas-max-h:62dvh] lg:[--canvas-max-h:70dvh]"
        onContextMenu={(event) => event.preventDefault()}
      >
        <img
          src={photo.url}
          alt="Your room"
          className="pointer-events-none absolute inset-0 h-full w-full object-contain"
          draggable={false}
        />
        {overlayUrl && (
          <img
            src={overlayUrl}
            alt="Preview of the change"
            className="anim-fade-in pointer-events-none absolute inset-0 h-full w-full object-contain"
            style={compare ? { clipPath: `inset(0 0 0 ${sliderX * 100}%)` } : undefined}
            draggable={false}
          />
        )}

        {/* The spotlight fades as a whole layer; the compositor does the animation. */}
        <canvas
          ref={spotRef}
          aria-hidden
          data-testid="spotlight"
          data-active={showSpotlight ? 'on' : 'off'}
          className="pointer-events-none absolute inset-0 h-full w-full transition-opacity duration-500"
          style={{ width: box.width, height: box.height, opacity: showSpotlight ? 1 : 0 }}
        />

        <canvas
          ref={overlayRef}
          className="absolute inset-0 h-full w-full"
          style={{
            width: box.width,
            height: box.height,
            cursor: compare ? 'ew-resize' : mode === 'browse' ? 'pointer' : 'crosshair',
          }}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerCancel={handlePointerUp}
        />

        {/* One named chip per found item — the interaction the screen exists for. Real
            buttons, not canvas text: focusable, 44px targets, and they read their names out.
            They pop in one after another, and none of them may cover another. */}
        {showChips &&
          photoMasks
            .filter((mask) => mask.label || mask.slot)
            .map((mask, index) => {
              const spot = chipSpots.get(mask.id)
              if (!spot) return null
              const active = mask.id === activeMaskId
              return (
                <button
                  key={mask.id}
                  type="button"
                  data-mask-id={mask.id}
                  onClick={() => onSelectMask(active ? null : mask.id)}
                  disabled={disabled}
                  style={{
                    left: `${spot.x}px`,
                    top: `${spot.y}px`,
                    animationDelay: `${Math.min(index * 70, 500)}ms`,
                  }}
                  className={`anim-chip-in absolute min-h-11 -translate-x-1/2 -translate-y-1/2 whitespace-nowrap rounded-pill border px-3.5 text-xs font-bold shadow-lift transition-all duration-200 ${
                    active
                      ? 'scale-105 border-blue bg-blue text-white shadow-blue'
                      : 'border-white/60 bg-white/95 text-ink backdrop-blur-sm hover:bg-white'
                  }`}
                >
                  {prettyLabel(mask.label ?? mask.slot ?? '')}
                </button>
              )
            })}

        {compare && overlayUrl && (
          <>
            <div
              className="pointer-events-none absolute inset-y-0 w-0.5 bg-white/90 shadow-lift"
              style={{ left: `${sliderX * 100}%` }}
            />
            {/* Clamped inside the frame: at the extremes the old handle slid half out of the
                photo and clipped (2026-08-23 field screenshots). */}
            <div
              aria-hidden
              data-testid="compare-handle"
              className="pointer-events-none absolute top-1/2 grid size-11 -translate-x-1/2 -translate-y-1/2 place-items-center rounded-full border border-line bg-white/95 text-sm font-bold text-ink shadow-lift"
              style={{ left: `${handleLeft}px` }}
            >
              ⟷
            </div>
            <input
              type="range"
              min={0}
              max={100}
              value={Math.round(sliderX * 100)}
              onChange={(event) => setSliderX(Number(event.target.value) / 100)}
              aria-label="Compare before and after"
              className="compare-range absolute inset-x-6 bottom-4 h-11 cursor-ew-resize appearance-none rounded-pill bg-transparent"
            />
          </>
        )}

        {/* Detection: a soft light sweeps the photo while the room is being read. */}
        {detecting && photoMasks.length === 0 && !compare && (
          <>
            <div aria-hidden className="anim-scan pointer-events-none absolute inset-x-0 h-24" />
            <div className="pointer-events-none absolute left-1/2 top-4 flex max-w-[92%] -translate-x-1/2 items-center gap-2 whitespace-nowrap rounded-pill bg-navy/85 px-4 py-2 text-xs font-semibold text-white">
              Finding what’s in your photo
              <Dots />
            </div>
          </>
        )}

        {/* A render in flight: shimmer, and say so on the photo itself. */}
        {rendering && !compare && (
          <>
            <div aria-hidden className="anim-shimmer pointer-events-none absolute inset-0" />
            <div className="pointer-events-none absolute left-1/2 top-4 flex max-w-[92%] -translate-x-1/2 items-center gap-2 whitespace-nowrap rounded-pill bg-navy/85 px-4 py-2 text-xs font-semibold text-white">
              Making your preview
              <Dots />
            </div>
          </>
        )}
      </div>

      {mode !== 'browse' && hasStrokes && (
        <div className="anim-rise mt-3 flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => void commit()}
            className="min-h-11 rounded-btn bg-blue px-5 font-semibold text-white shadow-blue"
          >
            Use this selection
          </button>
          <button
            type="button"
            onClick={clear}
            className="min-h-11 rounded-btn border border-line bg-white px-4 font-semibold text-ink"
          >
            Start over
          </button>
        </div>
      )}
    </div>
  )
}

function Dots() {
  return (
    <span aria-hidden className="inline-flex gap-0.5">
      <span className="anim-dot size-1 rounded-full bg-white" />
      <span className="anim-dot size-1 rounded-full bg-white [animation-delay:200ms]" />
      <span className="anim-dot size-1 rounded-full bg-white [animation-delay:400ms]" />
    </span>
  )
}

function drawAlpha(
  context: CanvasRenderingContext2D,
  alpha: Uint8Array,
  width: number,
  height: number,
  box: { offsetX: number; offsetY: number; scale: number },
): void {
  const buffer = document.createElement('canvas')
  buffer.width = width
  buffer.height = height
  const bufferContext = buffer.getContext('2d')
  if (!bufferContext) return

  const image = bufferContext.createImageData(width, height)
  // Blue at a third opacity inside the selection, with a hard white edge where a set pixel
  // touches an unset one.
  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      const index = y * width + x
      if (!alpha[index]) continue
      const edge =
        x === 0 ||
        y === 0 ||
        x === width - 1 ||
        y === height - 1 ||
        !alpha[index - 1] ||
        !alpha[index + 1] ||
        !alpha[index - width] ||
        !alpha[index + width]
      const offset = index * 4
      if (edge) {
        image.data[offset] = 255
        image.data[offset + 1] = 255
        image.data[offset + 2] = 255
        image.data[offset + 3] = 235
      } else {
        image.data[offset] = 45
        image.data[offset + 1] = 108
        image.data[offset + 2] = 223
        image.data[offset + 3] = 86
      }
    }
  }
  bufferContext.putImageData(image, 0, 0)
  context.drawImage(buffer, box.offsetX, box.offsetY, width * box.scale, height * box.scale)
}
